<template>
  <div class="order-option">
    <a
      :href="orderUrl"
      class="order-link"
      :aria-label="accessibilityLabel('caiAccessibilityOrderText', number)">
      <div class="order-summary">
        <div class="order-info">
          <div class="order-number">{{ number }}</div>
          <div class="order-date">
            <template v-if="tStatic('caiOrderDateLabel')">{{ tStatic('caiOrderDateLabel') }}: </template>{{ displayDate || formatDate(date) }}
          </div>
          <div class="order-status" :class="statusConfig.className">
            <oie-base-icon :icon="statusConfig.icon" size="xs"></oie-base-icon>
            <span>{{ statusLabel }}</span>
          </div>
        </div>
        <div v-if="hasTotal" class="order-total">
          <div class="order-total-price">{{ formatPrice(total, currency) }}</div>
          <div class="vat-label">{{ tStatic('caiOrderTotalLabel') }}</div>
        </div>
      </div>
      <div v-if="(invoices && invoices.length > 0) || trackingUrl" class="order-actions">
        <div class="order-actions-list">
          <a
            v-for="invoice in invoices"
            :key="invoice.number"
            :href="invoice.pdfUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="order-action"
            :aria-label="accessibilityLabel('caiAccessibilityDownloadInvoiceText', invoice.number)"
            @click.stop.prevent="handleInvoiceClick(invoice)">
            <oie-base-icon icon="document" size="xs"></oie-base-icon>
            <span>{{ tStatic('caiInvoice') }} {{ invoice.number }}</span>
            <oie-base-icon icon="arrow-right" size="xxs"></oie-base-icon>
          </a>
          <a
            v-if="trackingUrl"
            :href="trackingUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="order-action"
            :aria-label="accessibilityLabel('caiAccessibilityTrackShipmentText', trackingNumber)"
            @click.stop.prevent="handleTrackingClick">
            <oie-base-icon icon="delivery-truck" size="xs"></oie-base-icon>
            <span>{{ tStatic('caiShipment') }}</span>
            <oie-base-icon icon="arrow-right" size="xxs"></oie-base-icon>
          </a>
        </div>
      </div>
    </a>
  </div>
</template>

<script>

export default {
  props: {
    sessionId: {
      type: String,
      required: true
    },
    number: {
      type: String,
      required: true
    },
    date: {
      type: String,
      required: true
    },
    displayDate: {
      type: String,
      default: ''
    },
    statusKey: {
      type: String,
      default: 'unknown'
    },
    total: {
      type: Number,
      default: null
    },
    currency: {
      type: String,
      default: 'EUR'
    },
    goodsInformationNo: {
      type: String,
      default: ''
    },
    shipmentId: {
      type: String,
      default: ''
    },
    trackingUrl: {
      type: String,
      default: ''
    },
    invoices: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    hasTotal() {
      return typeof this.total === 'number'
    },
    trackingNumber() {
      return this.goodsInformationNo || this.shipmentId
    },
    orderUrl() {
      return `/my-pages/order-part-page/?orderNumber=${this.number}`
    },
    statusConfig() {
      const statusMap = {
        open: { className: 'status-open', icon: 'time' },
        openPartiallyDelivered: { className: 'status-partially-delivered', icon: 'subtract-alt' },
        released: { className: 'status-released', icon: 'checkmark-filled' }
      }
      return statusMap[this.statusKey] || { className: 'status-default', icon: 'information' }
    },
    statusLabel() {
      const capitalizedStatus = this.statusKey.charAt(0).toUpperCase() + this.statusKey.slice(1)
      return this.tStatic(`caiOrderStatus${capitalizedStatus}`)
    }
  },
  methods: {
    // Returns null when the label is not set in the CMS so the link falls back to its visible text.
    accessibilityLabel(key, number) {
      const template = this.tStatic(key)
      return template ? template.replace('{number}', number || '').trim() : null
    },
    // Same locale as the order page's formatPrice (core/localization.js), but keeps the order's own currency
    // because restored chats can outlive a market switch.
    formatPrice(price, currency) {
      if (!currency || !this.clientModel.region) {
        return price
      }
      return new Intl.NumberFormat(this.clientModel.region, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      }).format(price)
    },
    // Fallback for cards restored from local storage that were saved before the server sent displayDate.
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return new Intl.DateTimeFormat(this.clientModel.region, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date)
    },
    handleInvoiceClick(invoice) {
      window.open(invoice.pdfUrl, '_blank')
    },
    handleTrackingClick() {
      window.open(this.trackingUrl, '_blank', 'noopener,noreferrer')
    }
  }
}
</script>

<style scoped lang="scss">
.order-option {
  background-color: #fff;
  border: 1px solid #d9d9d9;
  margin-bottom: 0.5rem;
  border-radius: 2px;
  overflow: hidden;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: rgba(0, 91, 187, 0.5);
  }
}

.order-link {
  display: block;
  padding: 0.75rem;
  color: #000;
  text-decoration: none;
  width: 100%;
}

.order-summary {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 0.5rem;
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.order-number {
  font-size: 0.8rem;
  font-weight: 700;
  color: #1a1a1a;
}

.order-date {
  font-size: 0.75rem;
  color: color(brand-blue-3);
}

.order-status {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.25rem;
  font-size: 0.75rem;
}

.status-open {
  color: color(brand-blue-3);
}

.status-partially-delivered {
  color: #c77700;
}

.status-released {
  color: #2e7d32;
}

.status-default {
  color: #616161;
}

.order-total {
  text-align: right;
  white-space: nowrap;
}

.order-total-price {
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.2;
  color: color(brand-blue-3);
}

.vat-label {
  font-size: 0.7rem;
  color: #1a3a5c;
}

.order-actions {
  margin-top: 0.625rem;
  padding-top: 0.625rem;
  border-top: 1px solid #8c8c8c;
}

.order-actions-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: #edf4fb;
}

.order-action {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: color(brand-blue-3);
  text-decoration: none;
  background-color: #fff;
  border: 1px solid color(brand-blue-3);
  border-radius: 2px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e3eef9;
  }

  &:focus {
    outline: 2px solid color(brand-blue-3);
    outline-offset: 1px;
  }
}
</style>
